/**
 * PreconditionCard
 * Renders a single precondition: label + optional remediation CTA.
 * Satisfied preconditions are HIDDEN. Unsatisfied ones are ELEVATED with their CTA.
 * (BR-JO-08)
 */

import { CheckCircle2, AlertCircle } from 'lucide-react';
import { WorkflowActionButton } from './WorkflowActionButton';
import type { Precondition } from '../../types/workflow';

interface PreconditionCardProps {
  precondition: Precondition;
  index: number;
}

export function PreconditionCard({ precondition, index }: PreconditionCardProps) {
  // HIDE satisfied preconditions per BR-JO-08
  if (precondition.satisfied) {
    return null;
  }

  return (
    <div
      data-testid={`workflow-precondition-${index}`}
      className="flex items-start gap-3 p-3 rounded-lg border border-warning/40 bg-warning/5"
      role="listitem"
    >
      <AlertCircle
        className="h-4 w-4 text-warning shrink-0 mt-0.5"
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{precondition.label}</p>
        {precondition.remediation && (
          <div className="mt-2">
            <WorkflowActionButton
              action={precondition.remediation}
              testId={`workflow-precondition-cta-${index}`}
              disabled={false}
              size="sm"
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface StepCardProps {
  precondition: Precondition;
  /** Original index in member.preconditions — keeps the cta testid stable for deep-links. */
  index: number;
  /** 1-based position among steps — the visible number, matching the saga member badges. */
  stepNumber: number;
}

/**
 * StepCard
 * A per-instance STEP (kind='step'): unlike a prerequisite, it stays visible once satisfied,
 * rendered as a ✓ "Done" row so the landlord can see progress. Steps carry a numbered badge that
 * mirrors the saga member chain (1, 2, 3 …) for visual consistency. Unsatisfied steps render with
 * a remediation CTA, reusing the same cta testid so deep-links keep working.
 */
export function StepCard({ precondition, index, stepNumber }: StepCardProps) {
  // Numbered badge mirroring MemberChainView's position badge: green once done, muted while to-do.
  const badge = (
    <span
      className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${
        precondition.satisfied ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'
      }`}
      aria-hidden="true"
    >
      {stepNumber}
    </span>
  );

  if (precondition.satisfied) {
    return (
      <div
        data-testid={`workflow-step-${index}`}
        className="flex items-center gap-3 p-3 rounded-lg border border-success/30 bg-success/5"
        role="listitem"
      >
        {badge}
        <p className="text-sm font-medium text-foreground flex-1 min-w-0">{precondition.label}</p>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          Done
        </span>
      </div>
    );
  }

  return (
    <div
      // A STEP container (matches the satisfied branch's workflow-step-* prefix). Distinct from
      // PreconditionCard's workflow-precondition-* so prereq-counting locators don't pick up steps.
      // The CTA keeps the workflow-precondition-cta-* testid below — deep-links depend on it.
      data-testid={`workflow-step-${index}`}
      className="flex items-start gap-3 p-3 rounded-lg border border-warning/40 bg-warning/5"
      role="listitem"
    >
      <span className="mt-0.5">{badge}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{precondition.label}</p>
        {precondition.remediation && (
          <div className="mt-2">
            <WorkflowActionButton
              action={precondition.remediation}
              testId={`workflow-precondition-cta-${index}`}
              disabled={false}
              size="sm"
            />
          </div>
        )}
      </div>
    </div>
  );
}

